import { computed, ref, watch } from 'vue';
import { getEventFilterOptions } from '@/js/core/utils/events-api';
import {
  calendarFilterGroups,
  orderCalendarFilterGroups,
} from '@/js/core/utils/calendar-filter-groups';

export function useCalendarFilterOptions(
  dateRanges,
  { requestParams = {}, groupIds = null, enabled = null } = {}
) {
  const filterGroups = ref(calendarFilterGroups);
  const monthOptions = ref([]);
  const academicYears = ref([]);
  const isLoading = ref(false);
  const error = ref('');

  const requestedGroupIds = computed(() => {
    const value = groupIds?.value || groupIds;

    return Array.isArray(value) && value.length ? value : null;
  });
  const additionalParams = () => requestParams || {};
  // Held off while an index page sits behind a full-page modal; see useCalendarEvents.
  const isEnabled = () => (enabled ? enabled.value !== false : true);

  // Mirrors the watch sources below, so the key changes exactly when a refetch is triggered.
  const currentKey = computed(() => JSON.stringify([dateRanges.value, requestedGroupIds.value]));
  const loadedKey = ref('');
  // Requests can resolve out of order, so only the most recently issued one may write state.
  let requestSequence = 0;

  // False while the options on hand describe a previous range, so callers holding a rendered
  // month can wait for the groups that match it rather than re-grouping twice.
  const isCurrentRangeLoaded = computed(() => loadedKey.value === currentKey.value);

  watch(
    [dateRanges, requestedGroupIds, () => isEnabled()],
    async () => {
      if (!isEnabled()) {
        return;
      }

      const key = currentKey.value;
      const sequence = (requestSequence += 1);
      const isLatest = () => sequence === requestSequence;

      isLoading.value = true;
      error.value = '';

      try {
        const response = await getEventFilterOptions({
          ...additionalParams(),
          date_ranges: dateRanges.value,
        });

        if (!isLatest()) {
          return;
        }

        const groups = Array.isArray(response.data?.groups) ? response.data.groups : [];

        filterGroups.value = orderCalendarFilterGroups(filterGroupsForRequestedIds(groups));
        monthOptions.value = Array.isArray(response.data?.months) ? response.data.months : [];

        academicYears.value = Array.isArray(response.data?.academic_years)
          ? response.data.academic_years
          : [];
      } catch (requestError) {
        if (!isLatest()) {
          return;
        }

        console.error('Calendar filter options failed to load.', requestError);
        error.value = 'Could not load filter options.';
      } finally {
        if (isLatest()) {
          // Settled either way: a failed options request keeps the previous groups, but must
          // not leave callers that gate on this waiting forever.
          loadedKey.value = key;
          isLoading.value = false;
        }
      }
    },
    { deep: true, immediate: true }
  );

  function filterGroupsForRequestedIds(groups) {
    if (!requestedGroupIds.value) {
      return groups.length ? groups : calendarFilterGroups;
    }

    const selected = new Set(requestedGroupIds.value);
    const filtered = groups.filter(group => selected.has(group.id));

    return filtered.length
      ? filtered
      : calendarFilterGroups.filter(group => selected.has(group.id));
  }

  return {
    filterGroups,
    monthOptions,
    academicYears,
    isLoading,
    isCurrentRangeLoaded,
    error,
  };
}
